import React, { useEffect, useMemo, useState } from 'react';
import './StudioConfigurationView.css';

const CHAT_API_BASE = '/api/chat';
const SETUP_FEE_USD = 400;

// Default sidebar picks — the modules a fresh Atelier tenant sees out of
// the box. Users can toggle/reorder from the full list fetched below.
const DEFAULT_SIDEBAR = [
  'Accounts', 'Contacts', 'Leads', 'Project',
  'AOS_Products', 'AOS_Quotes', 'AOS_Invoices',
  'jcrm_PurchaseOrders', 'jcrm_Accounting',
];

const emptyUserRow = () => ({ name: '', email: '', role: 'user' });

const StudioConfigurationView = ({ user, onBack }) => {
  const [step, setStep] = useState('intro'); // intro | form | submitting | error
  const [availableModules, setAvailableModules] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [users, setUsers] = useState([emptyUserRow()]);
  const [sidebarOrder, setSidebarOrder] = useState(DEFAULT_SIDEBAR);
  const [errorMessage, setErrorMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Fetch the full module catalogue for the sidebar picker.
  useEffect(() => {
    if (step !== 'form') return;
    let cancelled = false;
    fetch(`${CHAT_API_BASE}/sidebar-config.php?action=modules`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.modules)) {
          setAvailableModules(data.modules);
          // Filter the default order down to modules that actually exist
          // for this instance (an ACL may hide some).
          setSidebarOrder(prev => prev.filter(id => data.modules.some(m => m.id === id)));
        }
      })
      .catch(() => { /* leave list empty; the user can still submit */ });
    return () => { cancelled = true; };
  }, [step]);

  const selectedIds = useMemo(() => new Set(sidebarOrder), [sidebarOrder]);

  const toggleModule = (id) => {
    setSidebarOrder(prev => selectedIds.has(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const moveModule = (idx, delta) => {
    setSidebarOrder(prev => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const updateUser = (i, field, value) => {
    setUsers(prev => prev.map((u, idx) => idx === i ? { ...u, [field]: value } : u));
  };
  const addUser = () => setUsers(prev => [...prev, emptyUserRow()]);
  const removeUser = (i) => setUsers(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  // Every required field filled = enable Proceed to Payment.
  const isFormValid = useMemo(() => {
    if (!companyName.trim()) return false;
    if (users.length === 0) return false;
    for (const u of users) {
      if (!u.name.trim()) return false;
      if (!u.email.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email.trim())) return false;
    }
    if (sidebarOrder.length === 0) return false;
    return true;
  }, [companyName, users, sidebarOrder]);

  const handleSubmit = async () => {
    setValidationErrors([]);
    setErrorMessage(null);
    if (!isFormValid) return;
    setStep('submitting');
    try {
      const res = await fetch(`${CHAT_API_BASE}/studio-config-checkout.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          company_name: companyName.trim(),
          users: users.map(u => ({ name: u.name.trim(), email: u.email.trim(), role: u.role })),
          sidebar_modules: sidebarOrder,
          user_id: user?.id || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !data.checkout_url) {
        setStep('error');
        setValidationErrors(Array.isArray(data.errors) ? data.errors : []);
        setErrorMessage(data.message || `Payment session failed (HTTP ${res.status})`);
        return;
      }
      // Hand off to Stripe — the whole tab redirects. Stripe will bounce
      // back to /?studio_config=paid&session_id=... which App.js catches.
      window.location.assign(data.checkout_url);
    } catch (err) {
      setStep('error');
      setErrorMessage(err.message || 'Network error');
    }
  };

  // -------- Renderers --------

  const renderIntro = () => (
    <div className="sc-intro">
      <div className="sc-intro-icon" aria-hidden="true">🎛️</div>
      <h2 className="sc-title sc-title-centered">Studio Configuration</h2>

      <div className="sc-price-line">
        <span className="sc-price-amount">${SETUP_FEE_USD}</span>
        <span className="sc-price-suffix">one-time</span>
      </div>

      <p className="sc-lead sc-lead-centered">
        Personalized setup to align Atelier Studio with your established workflow.
      </p>

      <div className="sc-divider"><span>INCLUDED</span></div>

      <ul className="sc-included">
        <li>60-minute workflow consultation</li>
        <li>Account structure and workflow settings</li>
        <li>Proposal, invoice &amp; purchase order templates</li>
        <li>Team roles and permissions</li>
        <li>Branding configuration</li>
        <li>One follow-up review</li>
      </ul>

      <div className="sc-scope-note">
        Configuration is limited to supported Atelier Studio settings. New features,
        custom integrations and source-code changes require separate scoping and pricing.
      </div>

      <button type="button" className="sc-primary sc-primary-block" onClick={() => setStep('form')}>
        Request Configuration
      </button>

      <button type="button" className="sc-link" onClick={onBack}>Back to plans</button>
    </div>
  );

  const renderForm = () => (
    <div className="sc-form">
      <div className="sc-form-header">
        <h2 className="sc-title">Configuration details</h2>
        <p className="sc-lead">
          Fill in your instance preferences. On submit we'll take secure payment for the
          <strong> ${SETUP_FEE_USD} </strong> setup fee via Stripe.
        </p>
      </div>

      {errorMessage && (
        <div className="sc-error-banner">
          <strong>Couldn't start payment.</strong> {errorMessage}
          {validationErrors.length > 0 && (
            <ul>{validationErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
        </div>
      )}

      <section className="sc-section">
        <label className="sc-label" htmlFor="sc-company">Company name</label>
        <input
          id="sc-company"
          type="text"
          className="sc-input"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          placeholder="e.g. Acme Interiors"
          maxLength={255}
        />
      </section>

      <section className="sc-section">
        <div className="sc-section-head">
          <h3 className="sc-section-title">Users</h3>
          <button type="button" className="sc-add-btn" onClick={addUser}>+ Add user</button>
        </div>
        <div className="sc-users">
          {users.map((u, i) => (
            <div key={i} className="sc-user-row">
              <input
                type="text"
                className="sc-input"
                placeholder="Full name"
                value={u.name}
                onChange={e => updateUser(i, 'name', e.target.value)}
              />
              <input
                type="email"
                className="sc-input"
                placeholder="email@company.com"
                value={u.email}
                onChange={e => updateUser(i, 'email', e.target.value)}
              />
              <select
                className="sc-input"
                value={u.role}
                onChange={e => updateUser(i, 'role', e.target.value)}
              >
                <option value="admin">Admin</option>
                <option value="user">User</option>
                <option value="viewer">Viewer</option>
              </select>
              {users.length > 1 && (
                <button
                  type="button"
                  className="sc-remove-btn"
                  aria-label="Remove user"
                  onClick={() => removeUser(i)}
                >×</button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="sc-section">
        <div className="sc-section-head">
          <h3 className="sc-section-title">Left sidebar tabs</h3>
          <span className="sc-hint">Reorder with ↑ / ↓ — top of the list appears first.</span>
        </div>

        {sidebarOrder.length === 0 && (
          <p className="sc-empty-list">No tabs selected yet — pick from the list below.</p>
        )}

        <ol className="sc-sidebar-list">
          {sidebarOrder.map((id, idx) => {
            const mod = availableModules.find(m => m.id === id);
            return (
              <li key={id} className="sc-sidebar-item selected">
                <span className="sc-sidebar-icon">{mod?.icon || '•'}</span>
                <span className="sc-sidebar-name">{mod?.name || id}</span>
                <div className="sc-sidebar-actions">
                  <button type="button" onClick={() => moveModule(idx, -1)} disabled={idx === 0} aria-label="Move up">↑</button>
                  <button type="button" onClick={() => moveModule(idx, 1)} disabled={idx === sidebarOrder.length - 1} aria-label="Move down">↓</button>
                  <button type="button" className="sc-sidebar-remove" onClick={() => toggleModule(id)} aria-label="Remove tab">×</button>
                </div>
              </li>
            );
          })}
        </ol>

        {availableModules.length > 0 && (
          <>
            <div className="sc-sidebar-subhead">Available tabs</div>
            <div className="sc-sidebar-catalog">
              {availableModules
                .filter(m => !selectedIds.has(m.id))
                .map(m => (
                  <button
                    type="button"
                    key={m.id}
                    className="sc-sidebar-chip"
                    onClick={() => toggleModule(m.id)}
                  >
                    <span className="sc-sidebar-icon">{m.icon || '•'}</span>
                    <span className="sc-sidebar-name">{m.name}</span>
                    <span className="sc-sidebar-add">Add</span>
                  </button>
                ))}
            </div>
          </>
        )}
      </section>

      <div className="sc-cta-row sc-cta-row-form">
        <button type="button" className="sc-secondary" onClick={() => setStep('intro')}>Back</button>
        <button
          type="button"
          className="sc-primary"
          onClick={handleSubmit}
          disabled={!isFormValid}
          title={isFormValid ? '' : 'Fill in every required field to proceed'}
        >
          Pay ${SETUP_FEE_USD} &amp; submit configuration
        </button>
      </div>
    </div>
  );

  const renderSubmitting = () => (
    <div className="sc-status">
      <div className="sc-spinner" aria-hidden="true"></div>
      <p>Creating your secure payment session…</p>
    </div>
  );

  const renderError = () => (
    <div className="sc-status sc-status-error">
      <div className="sc-status-icon" aria-hidden="true">⚠️</div>
      <h3>Something went wrong</h3>
      <p>{errorMessage || 'Unknown error.'}</p>
      {validationErrors.length > 0 && (
        <ul className="sc-error-list">
          {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      <div className="sc-cta-row">
        <button type="button" className="sc-secondary" onClick={onBack}>Back to plans</button>
        <button type="button" className="sc-primary" onClick={() => setStep('form')}>Try again</button>
      </div>
    </div>
  );

  return (
    <div className="sc-view">
      {step === 'intro' && renderIntro()}
      {step === 'form' && renderForm()}
      {step === 'submitting' && renderSubmitting()}
      {step === 'error' && renderError()}
    </div>
  );
};

export default StudioConfigurationView;
